import React, { useMemo, useRef, useState } from "react";

// Quita tildes y pasa a minúsculas para buscar sin importar cómo se escriba.
const normalizar = (texto) =>
  String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const MAX_RESULTADOS = 50;

// Selector de persona con buscador: se escribe parte del nombre (o del correo
// o el cargo) y la lista se filtra al instante — mucho más rápido que un
// desplegable largo cuando hay muchos usuarios registrados.
//   personas: [{ id, nombre, correo, cargo?, area_nombre? }]
//   valor: id seleccionado (string) o ""
//   onChange(idString)
function BuscadorPersona({ personas, valor, onChange, placeholder = "Escribe el nombre de la persona..." }) {
  const [consulta, setConsulta] = useState("");
  const [abierta, setAbierta] = useState(false);
  const [indiceActivo, setIndiceActivo] = useState(0);
  const inputRef = useRef(null);

  const seleccionada = personas.find((p) => String(p.id) === String(valor));

  const resultados = useMemo(() => {
    const palabras = normalizar(consulta).split(/\s+/).filter(Boolean);
    const lista = palabras.length
      ? personas.filter((p) => {
          const texto = normalizar(`${p.nombre} ${p.correo} ${p.cargo || ""} ${p.area_nombre || ""}`);
          return palabras.every((w) => texto.includes(w));
        })
      : personas;
    return lista;
  }, [personas, consulta]);

  const elegir = (persona) => {
    onChange(String(persona.id));
    setConsulta("");
    setAbierta(false);
  };

  const quitar = () => {
    onChange("");
    setConsulta("");
    setAbierta(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const alTeclear = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAbierta(true);
      setIndiceActivo((i) => Math.min(i + 1, Math.min(resultados.length, MAX_RESULTADOS) - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndiceActivo((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const elegida = resultados[indiceActivo];
      if (abierta && elegida) elegir(elegida);
    } else if (e.key === "Escape") {
      setAbierta(false);
    }
  };

  // Ya hay alguien elegido: se muestra como una tarjeta con opción de cambiar.
  if (seleccionada && !abierta) {
    return (
      <div style={estilos.elegida}>
        <span style={estilos.avatar}>{(seleccionada.nombre || "?").charAt(0).toUpperCase()}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={estilos.nombre}>{seleccionada.nombre}</span>
          <span style={estilos.correo}>
            {seleccionada.correo}
            {seleccionada.cargo ? ` · ${seleccionada.cargo}` : ""}
          </span>
        </span>
        <button type="button" onClick={quitar} style={estilos.botonCambiar}>
          Cambiar
        </button>
      </div>
    );
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="text"
        value={consulta}
        placeholder={placeholder}
        onChange={(e) => {
          setConsulta(e.target.value);
          setAbierta(true);
          setIndiceActivo(0);
        }}
        onFocus={() => setAbierta(true)}
        onKeyDown={alTeclear}
        style={estilos.input}
        autoComplete="off"
      />
      {abierta && (
        <div style={estilos.lista} role="listbox">
          {resultados.length === 0 ? (
            <div style={estilos.vacio}>Nadie coincide con “{consulta}”. Puedes crearla más abajo.</div>
          ) : (
            <>
              {resultados.slice(0, MAX_RESULTADOS).map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  role="option"
                  aria-selected={i === indiceActivo}
                  onMouseEnter={() => setIndiceActivo(i)}
                  onClick={() => elegir(p)}
                  style={{ ...estilos.opcion, ...(i === indiceActivo ? estilos.opcionActiva : {}) }}
                >
                  <span style={estilos.avatar}>{(p.nombre || "?").charAt(0).toUpperCase()}</span>
                  <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    <span style={estilos.nombre}>{p.nombre}</span>
                    <span style={estilos.correo}>
                      {p.correo}
                      {p.cargo ? ` · ${p.cargo}` : ""}
                    </span>
                  </span>
                </button>
              ))}
              <div style={estilos.pie}>
                {resultados.length > MAX_RESULTADOS
                  ? `Mostrando ${MAX_RESULTADOS} de ${resultados.length} — sigue escribiendo para afinar.`
                  : `${resultados.length} persona${resultados.length === 1 ? "" : "s"}`}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const estilos = {
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "0.5rem 0.7rem",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    fontFamily: "inherit",
    fontSize: "0.88rem",
  },
  lista: {
    marginTop: "0.35rem",
    border: "1px solid #e2e6ea",
    borderRadius: 10,
    background: "#fff",
    maxHeight: 230,
    overflowY: "auto",
    boxShadow: "0 4px 14px rgba(27,42,74,0.1)",
  },
  opcion: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    padding: "0.45rem 0.7rem",
    border: "none",
    borderBottom: "1px solid #f1f3f5",
    background: "#fff",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  opcionActiva: { background: "var(--tema-suave, #eef4fb)" },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "var(--tema-primario, #2f6690)",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.75rem",
    fontWeight: 700,
    flexShrink: 0,
  },
  nombre: { display: "block", fontSize: "0.88rem", fontWeight: 600, color: "var(--tema-titulo, #0b3c60)" },
  correo: { display: "block", fontSize: "0.76rem", color: "#8993a1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  vacio: { padding: "0.8rem", fontSize: "0.84rem", color: "#8993a1", textAlign: "center" },
  pie: { padding: "0.35rem 0.7rem", fontSize: "0.72rem", color: "#8993a1", background: "#fafbfc", position: "sticky", bottom: 0 },
  elegida: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    padding: "0.5rem 0.7rem",
    border: "1px solid #cfe6d8",
    background: "#f4f9f6",
    borderRadius: 10,
  },
  botonCambiar: {
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    border: "1px solid #d3d9df",
    borderRadius: 999,
    padding: "0.25rem 0.75rem",
    fontSize: "0.76rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
};

export default BuscadorPersona;
